import { cn } from '@/lib/utils';
import type { StateCopy, Tone } from '@/lib/copy';
import { Tooltip } from './ui/tooltip';

const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-ink-2',
  active: 'text-live',
  ok: 'text-ok',
  warn: 'text-warn',
  danger: 'text-danger',
  hidden: 'text-hidden',
};

const TONE_DOT: Record<Tone, string> = {
  neutral: 'bg-ink-3',
  active: 'bg-action',
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
  hidden: 'bg-hidden',
};

/* An indicator lamp, not a badge. No fill, no pill, no border: a 5px square of
   state colour beside mono text. Filled chips at this density turn a table into
   a colour chart and stop the eye finding the one row that matters.

   Copied from admin-dashboard/src/components/state-chip.tsx, with `hidden`
   added — the tone this dashboard needs and neither sibling has. */
export function StateChip({
  state,
  className,
  size = 'md',
}: {
  state: StateCopy;
  className?: string;
  size?: 'sm' | 'md';
}) {
  const chip = (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap font-mono',
        size === 'sm' ? 'text-tiny' : 'text-xs',
        TONE_TEXT[state.tone],
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'inline-block h-[5px] w-[5px] shrink-0 rounded-[1px]',
          TONE_DOT[state.tone],
          state.tone === 'active' && 'animate-notch-pulse',
        )}
      />
      {state.label}
    </span>
  );

  if (!state.detail) return chip;
  return <Tooltip label={state.detail}>{chip}</Tooltip>;
}

/** A count or a flag that is only worth showing when it is true. */
export function Flag({ label, tone = 'warn', title }: { label: string; tone?: Tone; title?: string }) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center whitespace-nowrap border px-1.5 py-px font-mono text-tiny uppercase tracking-[0.08em]',
        tone === 'danger' && 'border-danger text-danger',
        tone === 'warn' && 'border-warn text-warn',
        tone === 'ok' && 'border-ok text-ok',
        tone === 'active' && 'border-live text-live',
        tone === 'neutral' && 'border-rule-strong text-ink-3',
        tone === 'hidden' && 'border-hidden text-hidden',
      )}
    >
      {label}
    </span>
  );
}
